import React from 'react';
import { CircularProgress, TextField } from '@material-ui/core';
import { Autocomplete, AutocompleteRenderInputParams } from '@material-ui/lab';
import classNames from 'clsx';

import { StepSelectControlProps } from './StepSelectControl.interface';
import { useStyles } from './StepSelectControl.styles';
import { EndAdornment } from 'app/components/EndAdornment';
import { LabelEnum } from 'app/shared/enums';
import { SelectOptionsPaper } from 'app/components/SelectOptionsPaper';
import isMobileOrTablet from 'app/shared/helpers/isMobileOrTablet';

const PaperComponent: React.FC = ({ children }) => (
  <SelectOptionsPaper children={children} />
);

export function StepSelectControl({
  onChange,
  options,
  label,
  renderOption: Option,
  renderInputIcon: InputIcon,
  inputPlaceholder,
  value,
  open,
  loading,
  onOpen,
  onBlur,
  groupBy,
  error,
  inputHidden,
  endAdornmentHidden,
  CustomPopper,
  renderGroup,
  renderTags,
  openOnFocus,
  disabled,
}: StepSelectControlProps) {
  const classes = useStyles();

  const Input = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      id={`car-autocomplete`}
      label={label}
      variant="outlined"
      placeholder={inputHidden ? '' : inputPlaceholder}
      error={!disabled && error}
      disabled={disabled}
      inputProps={{
        ...params.inputProps,
        readOnly: isMobileOrTablet(),
      }}
      InputProps={{
        ...params.InputProps,
        startAdornment: (
          <>
            {params.InputProps.startAdornment}
            {!inputHidden && InputIcon && (
              <InputIcon className={classes.startAdornmentIcon} />
            )}
          </>
        ),
        endAdornment: (
          <>
            {loading ? <CircularProgress color={'primary'} size={20} /> : null}
            <EndAdornment open={open} hidden={endAdornmentHidden} />
          </>
        ),
        classes: {
          root: classes.textFieldRoot,
          notchedOutline: classes.notchedOutline,
          input: classNames({
            [classes.inputHidden]: inputHidden,
          }),
        },
      }}
    />
  );

  return (
    <Autocomplete
      disableClearable
      autoHighlight
      multiple
      openOnFocus={openOnFocus}
      value={value ? value : undefined}
      options={[...options, ...(value ? value : [])]}
      filterSelectedOptions
      groupBy={groupBy}
      open={open}
      onOpen={onOpen}
      getOptionLabel={(option) => option.value}
      loading={loading}
      renderOption={Option}
      renderInput={Input}
      renderTags={renderTags}
      renderGroup={renderGroup}
      onChange={onChange}
      onBlur={onBlur}
      loadingText={LabelEnum.Loading}
      PaperComponent={PaperComponent}
      PopperComponent={CustomPopper}
      className={classes.root}
      classes={{
        listbox: classes.listbox,
        option: classes.option,
        popperDisablePortal: classes.popperDisablePortal,
        popper: classes.popper,
      }}
      noOptionsText={LabelEnum.NoOptions}
      aria-label="car autocomplete"
      disabled={disabled}
    />
  );
}

export default StepSelectControl;
